export const SECTION_NAME = "WorkOrder";

export const INTRO_TEXT = `A work order is a directive to produce a specified number of items. SOS Inventory allows you to provide detailed instructions on the work order about how to make each item. Work orders are non-posting transactions. They do not affect your inventory until the manufacturing is recorded using a build or process transaction.`;

export const EXAMPLE_JSON = `{ 
  "id":123,
  "starred":0,
  "syncToken":0,
  "number":"WO1115",
  "date":"2019-09-30T18:59:00",
  "customer":{ 
    "id":244,
    "name":"Joe Smith"
  },
  "location":{ 
    "id":1,
    "name":"Texas"
  },
  "channel":null,
  "department":null,
  "priority":null,
  "assignedToUser":null,
  "linkedTransaction":null,
  "comment":"Sales Order #SO-11135",
  "customerNotes":"Notes",
  "customFields":[ 
    { 
      "id":31,
      "name":"Build/Field",
      "value":null,
      "dataType":"text"
    }
  ],
  "total":0.0,
  "closed":false,
  "archived":false,
  "summaryOnly":false,
  "hasSignature":false,
  "lines":[ 
    { 
      "id":290,
      "lineNumber":1,
      "item":{ 
        "id":1636,
        "name":"Widget"
      },
      "class":null,
      "job":null,
      "workcenter":null,
      "tax":null,
      "description":null,
      "quantity":1.00000,
      "weight":0.0000000000,
      "volume":0.0000000000,
      "weightunit":"lb",
      "volumeunit":"cbm",
      "unitprice":0.0,
      "amount":0.0,
      "altAmount":0.0,
      "picked":0.0,
      "shipped":0.0,
      "invoiced":0.0,
      "produced":0.00000,
      "returned":0.0,
      "cost":null,
      "margin":null,
      "listprice":null,
      "percentdiscount":null,
      "duedate":"2019-09-26T00:00:00",
      "uom":null,
      "bin":null,
      "lot":null,
      "serials":null
    }
  ]
}`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. Must not be provided on create transactions.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this transaction has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "number",
    type: "string",
    description: `The order number for this record.  If you wish to use the automatic numbering capability 
    on creation of an order, pass the string “auto”.`,
  },
  {
    name: "date",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Transaction date.`,
  },
  {
    name: "customer",
    type: "reference",
    description: "Customer for this transaction.",
  },
  {
    name: "location",
    type: "reference",
    description: `Location for this transaction.`,
  },
  {
    name: "channel",
    type: "reference",
    description: `Channel (e.g., Catalog, Retail Store) for this transaction.`,
  },
  {
    name: "department",
    type: "reference",
    description: `Department for this transaction.`,
  },
  {
    name: "priority",
    type: "reference",
    description:
      "The degree of importance or urgency assigned to this transaction.",
  },
  {
    name: "assignedToUser",
    type: "reference",
    description: `User to whom this transaction is assigned.`,
  },
  {
    name: "linkedTransaction",
    type: "linked transaction type",
    description: `The order linked to this transaction.`,
  },
  {
    name: "comment",
    type: "string",
    description:
      "The company’s internal comment about this transaction. This comment is not visible to the customer.",
  },
  {
    name: "customerNotes",
    type: "string",
    description: `Field for internal notes about customer.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "total",
    type: "number",
    description: "Total inputs on this work order.",
  },
  {
    name: "closed",
    type: "boolean",
    description: `True if transaction is closed, false if not.`,
    readOnly: true,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item is archived, false if not.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
  },
  {
    name: "hasSignature",
    type: "",
    description: `Reserved for future use.`,
  },
  {
    name: "lines",
    type: "array of work order lines",
    description: `The lines for this work order. See object structure below.`,
  },
];

export const LINE_ITEM = [
  {
    name: "id",
    type: "integer",
    description: `The unique identifier for this line item. ID field is ignored on create requests.`,
  },
  {
    name: "lineNumber",
    type: "integer",
    description: `The line number for this line.`,
  },
  {
    name: "item",
    type: "reference",
    description: `The line item to be made.`,
  },
  {
    name: "class",
    type: "reference",
    description: `The class for this line.`,
  },
  {
    name: "job",
    type: "reference",
    description: `The job for this line, if enabled.`,
  },
  {
    name: "workcenter",
    type: "reference",
    description: `The related work center for the job.`,
  },
  {
    name: "tax",
    type: "tax information object",
    description: "Unused.",
  },
  {
    name: "description",
    type: "string",
    description: `The instructions for this line.`,
  },
  {
    name: "quantity",
    type: "decimal",
    description: `The quantity for this line item.`,
  },
  {
    name: "weight",
    type: "decimal",
    description: `The weight of this line.`,
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: `The volume of this line.`,
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: `The unit for the item's weight value.`,
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `The unit for the item's volume value.`,
    readOnly: true,
  },
  {
    name: "unitPrice",
    type: "decimal",
    description: `The unit price for this item.  Must be above the item’s minimum price, if set.`,
  },
  {
    name: "amount",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "altAmount",
    type: "",
    description: "Unused.",
  },
  {
    name: "picked",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "shipped",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "invoiced",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "produced",
    type: "",
    description: `The number of items produced.`,
  },
  {
    name: "returned",
    type: "",
    description: "Unused.",
  },
  {
    name: "cost",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "margin",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "listPrice",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "percentDiscount",
    type: "decimal",
    description: "Unused.",
  },
  {
    name: "dueDate",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `The due date for this line.`,
  },
  {
    name: "uom",
    type: "reference",
    description: `The unit of measure for this line.`,
  },
  {
    name: "bin",
    type: "reference",
    description: "Unused.",
  },
  {
    name: "lot",
    type: "reference",
    description: "Unused.",
  },
  {
    name: "serials",
    type: "array of references",
    description: "Unused.",
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the estimate will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: number, customer name, or comments.`,
  },
  {
    name: "status",
    type: "string: closed/open",
    description: `Filters results by whether the transaction is open or closed.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "from/to",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS",
    description: `Returns records based on the beginning and ending transaction dates specified. Both parameters are optional. Using only one parameter allows filtering in one direction. Example:
    from=2019-09-01T00:00:00&to=2019-09-10T00:00:00
   `,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/workorder?start=101&maxresults=100&from=2019-09-01T00:00:00&to=2019-09-10T00:00:00&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";
